import { useMutation, useQueryClient } from '@tanstack/react-query';
import { T, useTranslate } from '@tolgee/react';
import { useFlag, useUnleashContext } from '@unleash/proxy-client-react';
import { Modal } from 'antd';
import { useRouter } from 'next/router';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useIntercom } from 'react-use-intercom';
import styled, { css } from 'styled-components';

import ChevronLeft from '../assets/icons/ChevronLeft';
import ChevronRight from '../assets/icons/ChevronRight';
import CuriRocketBigFlameSvg from '../assets/icons/CuriRocketBigFlameSvg';
import CuriRocketSvg from '../assets/icons/CuriRocketSvg';
import EmojiThanks from '../assets/icons/EmojiThanks';
import theme from '../assets/theme';
import useAccessBadge, {
  accessBadgeCacheKey,
  OrganizationType,
} from '../hooks/useAccessBadge';
import { useBreakPoint } from '../hooks/useBreakPoint';
import { useMetrics } from '../hooks/useMetrics';
import { teachingCreditStateKey } from '../hooks/useTeachingCreditState';
import useTrackOnboardingCompleted from '../hooks/useTrackOnboardingCompleted';
import { useWindowSize } from '../hooks/useWindowSize';
import BackendService from '../services/backendService/backendService';
import {
  GradeType,
  SchoolRoleType,
  SignUpOriginType,
  SubjectType,
} from '../services/backendService/types';
import { LangSelector } from '../utils/languageSelector';
import { configureStore } from '../utils/store';
import { Input } from './_atoms/_Input';
import { Button } from './_atoms/Button';
import { IconWrapper } from './_atoms/IconWrapper';
import OrganizationSelector, {
  SelectedLocationType,
  SelectedOrganizationType,
} from './_molecules/OrganizationSelector';
type CurrentForm =
  | 'personalInfo'
  | 'role'
  | 'selectOrganization'
  | 'subjects'
  | 'grades'
  | 'signUpOrigin'
  | 'studentJoinForm';

type FormSection = {
  title: React.ReactNode;
  options: { value: string; displayValue: React.ReactNode }[];
  canMultiSelect?: boolean;
  placeHolderText: string;
  label: React.ReactNode;
};

type ActiveFormSectionMapper = {
  [key in Exclude<
    CurrentForm,
    'personalInfo' | 'selectOrganization' | 'studentJoinForm'
  >]: FormSection;
};

const activeFormSectionMapper: ActiveFormSectionMapper = {
  role: {
    title: (
      <T keyName="What is your primary role?" defaultValue="What is your primary role?" />
    ),
    options: [
      { value: 'Teacher', displayValue: <T keyName="Teacher" defaultValue="Teacher" /> },
      {
        value: 'Instructional/Tech Coach',
        displayValue: (
          <T keyName="Instructional/Tech Coach" defaultValue="Instructional/Tech Coach" />
        ),
      },
      {
        value: 'Library Media Specialist',
        displayValue: (
          <T keyName="Library Media Specialist" defaultValue="Library Media Specialist" />
        ),
      },
      {
        value: 'School Administrator',
        displayValue: (
          <T keyName="School Administrator" defaultValue="School Administrator" />
        ),
      },
      {
        value: 'District Administrator',
        displayValue: (
          <T keyName="District Administrator" defaultValue="District Administrator" />
        ),
      },
      {
        value: 'Purchasing Manager',
        displayValue: (
          <T keyName="Purchasing Manager" defaultValue="Purchasing Manager" />
        ),
      },
      { value: 'Student', displayValue: <T keyName="Student" defaultValue="Student" /> },
      { value: 'Other', displayValue: <T keyName="Other" defaultValue="Other" /> },
    ],
    canMultiSelect: false,
    placeHolderText: 'Enter your role',
    label: 'Other role',
  },
  subjects: {
    title: (
      <T
        keyName="What subject(s) do you teach?"
        defaultValue="What subject(s) do you teach?"
      />
    ),
    options: [
      {
        value: 'English Language Arts',
        displayValue: (
          <T keyName="English Language Arts" defaultValue="English Language Arts" />
        ),
      },
      { value: 'Math', displayValue: <T keyName="Math" defaultValue="Math" /> },
      { value: 'Science', displayValue: <T keyName="Science" defaultValue="Science" /> },
      {
        value: 'Social Studies',
        displayValue: <T keyName="Social Studies" defaultValue="Social Studies" />,
      },
      { value: 'Other', displayValue: <T keyName="Other" defaultValue="Other" /> },
    ],
    canMultiSelect: true,
    placeHolderText: 'Enter your subject(s)',
    label: <T keyName="Other subject(s)" defaultValue="Other subject(s)" />,
  },
  grades: {
    title: (
      <T
        keyName="What grade(s) do you teach?"
        defaultValue="What grade(s) do you teach?"
      />
    ),
    options: [
      {
        value: 'Kindergarten',
        displayValue: <T keyName="Kindergarten" defaultValue="Kindergarten" />,
      },
      { value: '1st', displayValue: <T keyName="1st" defaultValue="1st" /> },
      { value: '2nd', displayValue: <T keyName="2nd" defaultValue="2nd" /> },
      { value: '3rd', displayValue: <T keyName="3rd" defaultValue="3rd" /> },
      { value: '4th', displayValue: <T keyName="4th" defaultValue="4th" /> },
      { value: '5th', displayValue: <T keyName="5th" defaultValue="5th" /> },
      { value: '6th', displayValue: <T keyName="6th" defaultValue="6th" /> },
      { value: '7th', displayValue: <T keyName="7th" defaultValue="7th" /> },
      { value: '8th', displayValue: <T keyName="8th" defaultValue="8th" /> },
      { value: '9th', displayValue: <T keyName="9th" defaultValue="9th" /> },
      { value: '10th', displayValue: <T keyName="10th" defaultValue="10th" /> },
      { value: '11th', displayValue: <T keyName="11th" defaultValue="11th" /> },
      { value: '12th', displayValue: <T keyName="12th" defaultValue="12th" /> },
      { value: '13th', displayValue: <T keyName="13th" defaultValue="13th" /> },
      { value: 'PD', displayValue: <T keyName="PD" defaultValue="PD" /> },
      {
        value: 'Higher Education',
        displayValue: <T keyName="Higher Education" defaultValue="Higher Education" />,
      },
      { value: 'Other', displayValue: <T keyName="Other" defaultValue="Other" /> },
    ],
    canMultiSelect: true,
    placeHolderText: 'Enter your grade(s)',
    label: <T keyName="Other grade(s)" defaultValue="Other grade(s)" />,
  },

  signUpOrigin: {
    title: (
      <T
        keyName="How did you hear about Curipod?"
        defaultValue="How did you hear about Curipod?"
      />
    ),
    options: [
      {
        value: 'Social Media',
        displayValue: <T keyName="Social Media" defaultValue="Social Media" />,
      },
      {
        value: 'Professional Development Session',
        displayValue: (
          <T
            keyName="Professional Development Session"
            defaultValue="Professional Development Session"
          />
        ),
      },
      {
        value: 'Coworker Referral',
        displayValue: <T keyName="Coworker Referral" defaultValue="Coworker Referral" />,
      },
      {
        value: 'Conference Presentation',
        displayValue: (
          <T keyName="Conference Presentation" defaultValue="Conference Presentation" />
        ),
      },
      { value: 'Other', displayValue: <T keyName="Other" defaultValue="Other" /> },
    ],
    canMultiSelect: false,
    placeHolderText: 'Tell us how you heard about Curipod',
    label: <T keyName="Other sign up origin" defaultValue="Other sign up origin" />,
  },
}; //  as const satisfies ActiveFormSectionMapper; // UGH

const ResponsiveInputWrapper = styled.div`
  display: inline-block;
  border: 1px solid #d9d9d9;
  border-radius: 5px;
  background-color: white;
  width: 350px;

  @media (max-width: 768px) {
    width: 280px;
  }

  @media (max-width: 480px) {
    width: 250px;
  }

  &:focus-within {
    border-color: ${theme.colors.orange};
    box-shadow: 0 0 0 2px rgba(255, 165, 0, 0.2);
  }

  .ant-input {
    border: none;
    box-shadow: none;
    width: 100%;

    &:focus {
      border: none;
      box-shadow: none;
    }

    &:hover {
      border: none;
      box-shadow: none;
    }
  }
`;

interface OnboardingModalProps {
  open: boolean;
  onClose: () => void;
  redirectUrl?: string;
  onComplete?: (selections: { subject?: string; grade?: string }) => void;
}

// Helper function to process user data and create profile payload
const processUserData = (userData: {
  firstName: string;
  lastName: string;
  role: string;
  subjects: SubjectType[] | undefined;
  grades: GradeType[] | undefined;
  signUpOrigin: string | undefined;
  customRole?: string;
  customSubjects?: string;
  customGrades?: string;
  customSignUpOrigin?: string;
}) => {
  // For multiselect fields, combine selected options with custom values
  let finalSubjects = userData.subjects;
  if (userData.subjects?.includes('Other') && userData.customSubjects?.trim()) {
    finalSubjects = [
      ...userData.subjects.filter(s => s !== 'Other'),
      userData.customSubjects.trim() as SubjectType,
    ];
  } else if (
    userData.customSubjects?.trim() &&
    (!userData.subjects || userData.subjects.length === 0)
  ) {
    finalSubjects = [userData.customSubjects.trim() as SubjectType];
  } else if (userData.subjects?.includes('Other') && !userData.customSubjects?.trim()) {
    finalSubjects = userData.subjects.filter(s => s !== 'Other');
    if (finalSubjects.length === 0) {
      finalSubjects = undefined;
    }
  }

  let finalGrades = userData.grades;
  if (userData.grades?.includes('Other') && userData.customGrades?.trim()) {
    finalGrades = [
      ...userData.grades.filter(g => g !== 'Other'),
      userData.customGrades.trim() as GradeType,
    ];
  } else if (
    userData.customGrades?.trim() &&
    (!userData.grades || userData.grades.length === 0)
  ) {
    finalGrades = [userData.customGrades.trim() as GradeType];
  } else if (userData.grades?.includes('Other') && !userData.customGrades?.trim()) {
    finalGrades = userData.grades.filter(g => g !== 'Other');
    if (finalGrades.length === 0) {
      finalGrades = undefined;
    }
  }

  // For single-select fields, use custom value if provided, otherwise use selected value
  const finalRole = userData.customRole?.trim()
    ? userData.customRole.trim()
    : userData.role;
  const finalSignUpOrigin = userData.customSignUpOrigin?.trim()
    ? userData.customSignUpOrigin.trim()
    : userData.signUpOrigin;

  return {
    finalSubjects,
    finalGrades,
    finalRole,
    finalSignUpOrigin,
  };
};

// Function to handle profile update
const submitProfileUpdate = async (userData: {
  firstName: string;
  lastName: string;
  role: string;
  subjects: SubjectType[] | undefined;
  grades: GradeType[] | undefined;
  signUpOrigin: string | undefined;
  customRole?: string;
  customSubjects?: string;
  customGrades?: string;
  customSignUpOrigin?: string;
}) => {
  const { finalSubjects, finalGrades, finalRole, finalSignUpOrigin } = processUserData(
    userData,
  );

  const editProfilePayload = {
    firstName: userData.firstName,
    lastName: userData.lastName,
    username: `${userData.firstName} ${userData.lastName}`.trim(),
    onboardingCompletedAt: new Date(),
    schoolRole: finalRole,
    subjects: finalSubjects,
    grades: finalGrades,
    signUpOrigin: finalSignUpOrigin,
  };

  return BackendService.instance.users.editProfile(editProfilePayload);
};

// Function to handle organization reselection
const submitOrganizationReselection = async (
  organizationData: {
    selectedOrganization: SelectedOrganizationType | null;
    customOrganizationName?: string;
    selectedLocation: SelectedLocationType | null;
    organizationType: OrganizationType | null;
  },
  shouldSkipOrganizationSelection: boolean,
  currentOrganizationId?: string,
) => {
  const shouldUpdateOrganization =
    organizationData.selectedOrganization && !shouldSkipOrganizationSelection;

  // Only reselect if the organization is different from the existing one
  const isOrganizationDifferent =
    organizationData.selectedOrganization &&
    currentOrganizationId !== organizationData.selectedOrganization.organization.id;

  if (shouldUpdateOrganization && isOrganizationDifferent) {
    const selectedOrg = organizationData.selectedOrganization!;
    const reselectionPayload = {
      newOrgId: !selectedOrg.organization.custom
        ? selectedOrg.organization.id
        : undefined,
      customOrgName: selectedOrg.organization.custom
        ? selectedOrg.organization.name
        : organizationData.customOrganizationName,
      city: selectedOrg.organization.city,
      region: selectedOrg.organization.region,
      country: selectedOrg.organization.country,
      primaryZipCode: organizationData.selectedLocation?.place?.zipCode,
      organizationType: organizationData.organizationType || undefined,
    };

    return BackendService.instance.organizations.reselectOrganization(reselectionPayload);
  }

  return Promise.resolve();
};

// Helper functions to map onboarding values to internal localStorage format
const mapSubjectToInternalValue = (subject: string): string => {
  const subjectMap: Record<string, string> = {
    'English Language Arts': 'ela',
    Math: 'math',
    Science: 'science',
    'Social Studies': 'socialStudies',
    Other: 'other',
  };
  return subjectMap[subject] || subject.toLowerCase();
};

const mapGradeToInternalValue = (grade: string): string => {
  const gradeMap: Record<string, string> = {
    Kindergarten: 'K',
    '1st': '1',
    '2nd': '2',
    '3rd': '3',
    '4th': '4',
    '5th': '5',
    '6th': '6',
    '7th': '7',
    '8th': '8',
    '9th': '9',
    '10th': '10',
    '11th': '11',
    '12th': '12',
    '13th': '13',
    PD: 'PD',
    'Higher Education': 'Higher Education',
  };
  return gradeMap[grade] || grade;
};

// Custom hook for onboarding submission with retry logic
const useOnboardingSubmission = (
  onClose: () => void,
  redirectUrl?: string,
  onComplete?: (selections: { subject?: string; grade?: string }) => void,
) => {
  const queryClient = useQueryClient();
  const router = useRouter();
  const metrics = useMetrics();

  const { mutate: submitOnboarding, isPending: isSubmitting, error } = useMutation({
    mutationFn: async ({
      userData,
      organizationData,
      shouldSkipOrganizationSelection,
      currentOrganizationId,
    }: {
      userData: {
        firstName: string;
        lastName: string;
        role: string;
        subjects: SubjectType[] | undefined;
        grades: GradeType[] | undefined;
        signUpOrigin: string | undefined;
        customRole?: string;
        customSubjects?: string;
        customGrades?: string;
        customSignUpOrigin?: string;
      };
      organizationData: {
        selectedOrganization: SelectedOrganizationType | null;
        customOrganizationName?: string;
        selectedLocation: SelectedLocationType | null;
        organizationType: OrganizationType | null;
      };
      shouldSkipOrganizationSelection: boolean;
      currentOrganizationId?: string;
    }) => {
      const {
        finalSubjects,
        finalGrades,
        finalRole,
        finalSignUpOrigin,
      } = processUserData(userData);

      const userInfo = {
        firstName: userData.firstName,
        lastName: userData.lastName,
        role: finalRole,
        subjects: finalSubjects ? JSON.stringify(finalSubjects) : undefined,
        grades: finalGrades ? JSON.stringify(finalGrades) : undefined,
        otherRole: userData.customRole?.trim() || undefined,
        otherSubjects: userData.customSubjects?.trim() || undefined,
        otherGrades: userData.customGrades?.trim() || undefined,
        organization: organizationData.selectedOrganization,
        signUpOrigin: finalSignUpOrigin,
      };

      // Log metrics
      metrics.logEvent('Onboarding.Submitted', {
        firstName: userData.firstName,
        lastName: userData.lastName,
        schoolRole: userInfo.role,
        subjects: userInfo.subjects,
        grades: userInfo.grades,
        otherRole: userInfo.otherRole,
        otherSubjects: userInfo.otherSubjects,
        otherGrades: userInfo.otherGrades,
        organization: userInfo.organization,
        signUpOrigin: userInfo.signUpOrigin || '',
        otherSignUpOrigin: userData.customSignUpOrigin?.trim() || '',
      });

      // Execute profile update and organization reselection independently
      const [profileUpdateResult, organizationReselectionResult] = await Promise.all([
        submitProfileUpdate(userData),
        submitOrganizationReselection(
          organizationData,
          shouldSkipOrganizationSelection,
          currentOrganizationId,
        ),
      ]);

      return organizationReselectionResult;
    },
    retry: 1,
    onSuccess: (organizationReselectionResult, variables) => {
      // Map onboarding selections to internal format for home page
      const { userData } = variables;
      const { finalSubjects, finalGrades } = processUserData(userData);

      const ELA_SUBJECT = 'English Language Arts';
      const preferredSubject = finalSubjects?.includes(ELA_SUBJECT)
        ? ELA_SUBJECT
        : finalSubjects?.[0];
      const internalSubject = preferredSubject
        ? mapSubjectToInternalValue(preferredSubject)
        : undefined;
      const internalGrade = finalGrades?.[0]
        ? mapGradeToInternalValue(finalGrades[0])
        : undefined;

      // Persist to localStorage for future page loads
      if (internalSubject) {
        localStorage.setItem(
          '@curipod_v1_home_subject',
          JSON.stringify({ value: internalSubject }),
        );
      }
      if (internalGrade) {
        localStorage.setItem(
          '@curipod_v1_home_grade',
          JSON.stringify({ value: internalGrade }),
        );
      }

      // Update home page state directly (no reload needed)
      onComplete?.({ subject: internalSubject, grade: internalGrade });

      queryClient.invalidateQueries({ queryKey: [accessBadgeCacheKey] });
      queryClient.invalidateQueries({ queryKey: ['tenants'] });
      queryClient.invalidateQueries({ queryKey: teachingCreditStateKey });

      if (organizationReselectionResult) {
        router.replace(`/${organizationReselectionResult.id}/home`);
      }
      onClose();
    },
    onError: async error => {
      console.error('Error submitting onboarding:', error);
      metrics.logEvent('Onboarding.Submission.Error', {
        error: error,
      });

      // Reset onboarding completion status so modal will show again next time
      try {
        await BackendService.instance.users.editProfile({
          onboardingCompletedAt: null,
        });
      } catch (resetError) {
        console.error('Error resetting onboarding status:', resetError);
      }
    },
  });

  return {
    submitOnboarding,
    isSubmitting,
    error,
  };
};

const OnboardingModal: React.FC<OnboardingModalProps> = ({
  open,
  onClose,
  redirectUrl,
  onComplete,
}) => {
  useUnleashContext();
  const { trackEvent } = useIntercom();

  // Monkey patch to handle Google Translate DOM manipulation errors
  useEffect(() => {
    const originalRemoveChild = Node.prototype.removeChild;
    const originalInsertBefore = Node.prototype.insertBefore;

    // @ts-ignore - Monkey patch to prevent Google Translate crashes
    Node.prototype.removeChild = function(child: Node) {
      if (child.parentNode === this) {
        return originalRemoveChild.call(this, child);
      }
      return child;
    };

    // @ts-ignore - Monkey patch to prevent Google Translate crashes
    Node.prototype.insertBefore = function(newNode: Node, referenceNode: Node | null) {
      if (referenceNode && referenceNode.parentNode === this) {
        return originalInsertBefore.call(this, newNode, referenceNode);
      }
      if (referenceNode) {
        return this.appendChild(newNode);
      }
      return originalInsertBefore.call(this, newNode, referenceNode);
    };

    return () => {
      // Clean up monkey patches when component unmounts
      Node.prototype.removeChild = originalRemoveChild;
      Node.prototype.insertBefore = originalInsertBefore;
    };
  }, []);

  const districtSignupEnabled = useFlag('orgPickerV2districtSignupEnabled');

  const metrics = useMetrics();
  const router = useRouter();
  const { accessBadge } = useAccessBadge();
  const [windowWidth, windowHeight] = useWindowSize();
  const queryClient = useQueryClient();

  // Use the custom onboarding submission hook
  const { submitOnboarding, isSubmitting, error } = useOnboardingSubmission(
    onClose,
    redirectUrl,
    onComplete,
  );

  // Track onboarding completion
  const { trackOnboardingCompleted } = useTrackOnboardingCompleted();

  // Personal info state
  const [firstName, setFirstName] = useState<string>('');
  const [lastName, setLastName] = useState<string>('');

  // Prepopulate firstName and lastName if they're already set in the user's data
  useEffect(() => {
    if (accessBadge?.user?.firstName && !firstName) {
      setFirstName(accessBadge.user.firstName);
    }
  }, [accessBadge?.user?.firstName]);

  useEffect(() => {
    if (accessBadge?.user?.lastName && !lastName) {
      setLastName(accessBadge.user.lastName);
    }
  }, [accessBadge?.user?.lastName]);

  // Form state
  const [role, setRole] = useState<SchoolRoleType>();
  const [customRole, setCustomRole] = useState<string>();
  const [subjects, setSubjects] = useState<SubjectType[] | undefined>();
  const [signUpOrigin, setSignUpOrigin] = useState<SignUpOriginType>();
  const [grades, setGrades] = useState<GradeType[] | undefined>();
  const [customSubjects, setCustomSubjects] = useState<string>();
  const [customGrades, setCustomGrades] = useState<string>();
  const [currentForm, setCurrentForm] = useState<CurrentForm>('personalInfo');
  const [customSignUpOrigin, setCustomSignUpOrigin] = useState<string>();

  const [
    selectedOrganization,
    setSelectedOrganization,
  ] = useState<SelectedOrganizationType | null>(null);
  const [organizationType, setOrganizationType] = useState<OrganizationType | null>(null);
  const [selectedLocation, setSelectedLocation] = useState<SelectedLocationType | null>(
    null,
  );
  const [hideOrganizationTypePicker, setHideOrganizationTypePicker] = useState<boolean>(
    false,
  );
  const [customOrganizationName, setCustomOrganizationName] = useState<string>();
  const [needsCustomOrganizationName, setNeedsCustomOrganizationName] = useState<boolean>(
    false,
  );
  const [locationFetchError, setLocationFetchError] = useState<string | undefined>();

  const shouldSkipOrganizationSelection = useMemo(() => {
    return (
      accessBadge?.organization?.source === 'Curipod' ||
      accessBadge?.organization?.source === 'MCH'
    );
  }, [accessBadge?.organization?.source]);

  const handleChangeOrgType = useCallback(
    (newOrgType: OrganizationType | null) => {
      if (organizationType && organizationType !== newOrgType) {
        setSelectedOrganization(null);
        setCustomOrganizationName(undefined);
        setNeedsCustomOrganizationName(false);
      }
      setOrganizationType(newOrgType);
    },
    [organizationType, setNeedsCustomOrganizationName],
  );

  const handleChangeLocation = (location: SelectedLocationType | null) => {
    if (
      selectedLocation?.placeAutocomplete?.place_id !==
      location?.placeAutocomplete?.place_id
    ) {
      setSelectedOrganization(null);
      setCustomOrganizationName(undefined);
      setNeedsCustomOrganizationName(false);
    }
    setSelectedLocation(location);
  };

  const handleChangeOrganization = (organization: SelectedOrganizationType | null) => {
    setSelectedOrganization(organization);
  };

  const isFinalForm = useMemo(() => {
    if (role === 'Student') {
      return currentForm === 'studentJoinForm';
    } else {
      return currentForm === 'signUpOrigin';
    }
  }, [currentForm, role]);

  const selectNextCurrentForm = useCallback(() => {
    switch (currentForm) {
      case 'personalInfo':
        setCurrentForm('role');
        break;
      case 'role':
        if (role === 'Student') {
          setCurrentForm('studentJoinForm');
          break;
        }

        if (shouldSkipOrganizationSelection) {
          if (role === 'Teacher') {
            setCurrentForm('subjects');
          } else {
            setCurrentForm('signUpOrigin');
          }
          break;
        }

        setCurrentForm('selectOrganization');
        if (!customOrganizationName) {
          setLocationFetchError(undefined);
          setNeedsCustomOrganizationName(false);
        }
        switch (role) {
          case 'Teacher':
          case 'School Administrator':
            handleChangeOrgType(OrganizationType.School);
            setHideOrganizationTypePicker(true);
            break;
          case 'District Administrator':
            handleChangeOrgType(OrganizationType.District);
            setHideOrganizationTypePicker(true);
            break;
          default:
            handleChangeOrgType(null);
            setHideOrganizationTypePicker(false);
            break;
        }
        break;
      case 'selectOrganization':
        if (role === 'Teacher') {
          setCurrentForm('subjects');
        } else {
          setCurrentForm('signUpOrigin');
        }
        break;
      case 'subjects':
        setCurrentForm('grades');
        break;
      case 'grades':
        setCurrentForm('signUpOrigin');
        break;
      default:
        break;
    }
  }, [
    currentForm,
    customOrganizationName,
    handleChangeOrgType,
    role,
    shouldSkipOrganizationSelection,
  ]);

  const selectPreviousForm = useCallback(
    (currentForm: CurrentForm) => {
      switch (currentForm) {
        case 'role':
          setCurrentForm('personalInfo');
          break;
        case 'studentJoinForm':
          setCurrentForm('role');
          break;
        case 'selectOrganization':
          setCurrentForm('role');
          break;
        case 'subjects':
          setSubjects(undefined);
          setCustomSubjects(undefined);
          if (shouldSkipOrganizationSelection) {
            setCurrentForm('role');
          } else {
            setCurrentForm('selectOrganization');
          }
          break;
        case 'grades':
          setGrades(undefined);
          setCustomGrades(undefined);
          setCurrentForm('subjects');
          break;
        case 'signUpOrigin':
          if (role === 'Teacher') {
            setGrades(undefined);
            setCustomGrades(undefined);
            setCurrentForm('grades');
          } else if (shouldSkipOrganizationSelection) {
            setCurrentForm('role');
          } else {
            setCurrentForm('selectOrganization');
          }
          break;
        default:
          break;
      }
    },
    [shouldSkipOrganizationSelection, role],
  );

  const handleGoBack = () => {
    selectPreviousForm(currentForm);
    metrics.logEvent('Onboarding.Back.Button.Clicked', {
      currentOnboardingPage: currentForm,
      from: 'onboarding',
    });
  };

  const canSkipCurrentForm = useMemo(() => {
    return (
      currentForm === 'grades' ||
      currentForm === 'subjects' ||
      currentForm === 'signUpOrigin'
    );
  }, [currentForm]);

  const handleGoForward = (isSkip?: boolean) => {
    if (isFinalForm) {
      handleSubmit();
      return;
    }

    if (isSkip) {
      metrics.logEvent('Onboarding.Skip.Button.Clicked', {
        currentOnboardingPage: currentForm,
      });
    } else {
      metrics.logEvent('Onboarding.Next.Button.Clicked', {
        currentOnboardingPage: currentForm,
        firstName,
        lastName,
        schoolRole: role ? role : '',
        subjects: subjects ? JSON.stringify(subjects) : '',
        grades: grades ? JSON.stringify(grades) : '',
        otherRole: customRole ? customRole : '',
        otherSubjects: customSubjects ? customSubjects : '',
        otherGrades: customGrades ? customGrades : '',
        organization: selectedOrganization?.organization
          ? selectedOrganization?.organization
          : '',
        signUpOrigin: signUpOrigin ? JSON.stringify(signUpOrigin) : '',
        otherSignUpOrigin: customSignUpOrigin || '',
      });
    }

    selectNextCurrentForm();
  };

  const handleSubmit = () => {
    // Prevent multiple submissions
    if (isSubmitting) return;

    // Fire off backend calls asynchronously in background
    submitOnboarding({
      userData: {
        firstName,
        lastName,
        role: role || '',
        subjects,
        grades,
        signUpOrigin,
        customRole,
        customSubjects,
        customGrades,
        customSignUpOrigin,
      },
      organizationData: {
        selectedOrganization,
        customOrganizationName,
        selectedLocation,
        organizationType,
      },
      shouldSkipOrganizationSelection,
      currentOrganizationId: accessBadge?.organization?.id,
    });
    trackOnboardingCompleted({
      userAgent: navigator.userAgent,
      eventType: 'OnboardingSubmitted',
      userRole: role || '',
    });
    trackEvent('OnboardingSubmitted', {
      userRole: role || '',
      subjects: subjects?.[0] || '',
      grades: grades?.[0] || '',
    });
  };

  const formNumber = useMemo(() => {
    const orgSelectionSkipped = shouldSkipOrganizationSelection;

    switch (currentForm) {
      case 'personalInfo':
        return 0;
      case 'role':
        return 1;
      case 'selectOrganization':
        return 2;
      case 'subjects':
        return orgSelectionSkipped ? 2 : 3;
      case 'grades':
        return orgSelectionSkipped ? 3 : 4;
      case 'signUpOrigin':
        return orgSelectionSkipped ? 4 : 5;
      default:
        return 0;
    }
  }, [currentForm, shouldSkipOrganizationSelection]);

  const doesNeedCustomOrganizationName = () => {
    if (!districtSignupEnabled && organizationType === OrganizationType.District) {
      return true;
    }
    return needsCustomOrganizationName || !!customOrganizationName;
  };

  const shouldAllowSearchRetry = () => {
    if (!districtSignupEnabled && organizationType === OrganizationType.District) {
      return false;
    }
    return true;
  };

  const { t } = useTranslate();

  const renderCurrentForm = () => {
    switch (currentForm) {
      case 'personalInfo':
        return (
          <FormContainer>
            <h1>
              <T
                keyName="Welcome! Let's get to know you"
                defaultValue="Welcome! Let's get to know you"
              />
              <IconWrapper
                height="2em"
                width="2em"
                style={{
                  verticalAlign: 'middle',
                  display: 'inline-block',
                }}
              >
                <EmojiThanks />
              </IconWrapper>
            </h1>
            <PersonalInfoContainer>
              <div>
                <p style={{ textAlign: 'left', width: '100%', marginBottom: 5 }}>
                  <T keyName="First Name" defaultValue="First Name" />
                </p>
                <ResponsiveInputWrapper>
                  <Input
                    style={{
                      fontSize: 20,
                      border: 'none',
                      boxShadow: 'none',
                    }}
                    autoFocus
                    placeholder={t('Enter your first name', 'Enter your first name')}
                    onChange={e => setFirstName(e.target.value)}
                    value={firstName}
                  />
                </ResponsiveInputWrapper>
              </div>
              <div>
                <p style={{ textAlign: 'left', width: '100%', marginBottom: 5 }}>
                  <T keyName="Last Name" defaultValue="Last Name" />
                </p>
                <ResponsiveInputWrapper>
                  <Input
                    style={{
                      fontSize: 20,
                      border: 'none',
                      boxShadow: 'none',
                    }}
                    placeholder={t('Enter your last name', 'Enter your last name')}
                    onChange={e => setLastName(e.target.value)}
                    value={lastName}
                  />
                </ResponsiveInputWrapper>
              </div>
            </PersonalInfoContainer>
            <LangSelector />
            <Button
              disabled={!firstName.trim() || !lastName.trim() || isSubmitting}
              loading={isSubmitting}
              onClick={() => handleGoForward()}
            >
              <T keyName="Next" defaultValue="Next" />
            </Button>
          </FormContainer>
        );

      case 'role':
        return (
          <FormContainer>
            <h1>{activeFormSectionMapper[currentForm].title}</h1>
            <OptionsContainer>
              {activeFormSectionMapper[currentForm].options &&
                activeFormSectionMapper[currentForm].options.map((option, i) => (
                  <StyledOption
                    $isSelected={role === option.value}
                    key={i}
                    onClick={() => {
                      metrics.logEvent('Onboarding.Role.Option.Selected', {
                        selectedOption: option.value,
                      });
                      setRole(option.value as SchoolRoleType);
                    }}
                  >
                    {option.displayValue}
                  </StyledOption>
                ))}
            </OptionsContainer>
            {role === 'Other' && (
              <div>
                <p style={{ textAlign: 'left', width: '100%', marginBottom: 0 }}>
                  {activeFormSectionMapper[currentForm].label}
                </p>
                <Input
                  maxLength={50}
                  style={{ width: 250, fontSize: 16, backgroundColor: '#fafafa' }}
                  placeholder={
                    t('Enter your role', 'Enter your role') ??
                    activeFormSectionMapper[currentForm].placeHolderText
                  }
                  onChange={e => setCustomRole(e.target.value)}
                  value={customRole}
                />
              </div>
            )}
            <Button
              disabled={
                role === undefined ||
                (role === 'Other' && !customRole?.trim()) ||
                isSubmitting
              }
              loading={isSubmitting}
              onClick={() => handleGoForward()}
            >
              <T keyName="Next" defaultValue="Next" />
            </Button>
          </FormContainer>
        );

      case 'selectOrganization':
        return (
          <FormContainer>
            {hideOrganizationTypePicker && organizationType ? (
              <h1>
                <T
                  keyName="Select your {organizationType}"
                  defaultValue="Select your {organizationType}"
                  params={{ organizationType: t(organizationType, organizationType) }}
                />
              </h1>
            ) : (
              <h1>
                <T
                  keyName="Select your School/District"
                  defaultValue="Select your School/District"
                />
              </h1>
            )}
            <OrganizationSelector
              organizationType={organizationType}
              onSelectOrganizationType={handleChangeOrgType}
              onSelectLocation={handleChangeLocation}
              selectedLocation={selectedLocation}
              onSelectOrganization={handleChangeOrganization}
              selectedOrganization={selectedOrganization}
              hideOrganizationTypePicker={hideOrganizationTypePicker}
              customOrganizationName={customOrganizationName}
              onSetCustomOrganizationName={setCustomOrganizationName}
              locationFetchError={locationFetchError}
              onLocationFetchError={setLocationFetchError}
              needsCustomOrganizationName={doesNeedCustomOrganizationName()}
              onSetNeedsCustomOrganizationName={setNeedsCustomOrganizationName}
              disableSearchRetry={!shouldAllowSearchRetry()}
              fluid
              metricEventNamePrefix="Onboarding"
            />

            <Button
              disabled={
                selectedLocation == undefined ||
                selectedOrganization == undefined ||
                isSubmitting
              }
              loading={isSubmitting}
              onClick={() => handleGoForward()}
              styles={{ marginTop: 20 }}
            >
              <T keyName="Next" defaultValue="Next" />
            </Button>
          </FormContainer>
        );

      case 'subjects':
        return renderMultiSelectForm(
          currentForm,
          subjects,
          setSubjects,
          customSubjects,
          setCustomSubjects,
          'Onboarding.Subject.Option.Selected',
          isSubmitting,
        );

      case 'grades':
        return renderMultiSelectForm(
          currentForm,
          grades,
          setGrades,
          customGrades,
          setCustomGrades,
          'Onboarding.Grade.Option.Selected',
          isSubmitting,
        );

      case 'signUpOrigin':
        return (
          <FormContainer>
            <h1>{activeFormSectionMapper[currentForm].title}</h1>
            <OptionsContainer>
              {activeFormSectionMapper[currentForm].options &&
                activeFormSectionMapper[currentForm].options.map((option, i) => (
                  <StyledOption
                    $isSelected={signUpOrigin === option.value}
                    key={i}
                    onClick={() => {
                      metrics.logEvent('Onboarding.SignUpOrigin.Option.Selected', {
                        selectedOption: option.value,
                      });
                      setSignUpOrigin(option.value as SignUpOriginType);
                    }}
                  >
                    {option.displayValue}
                  </StyledOption>
                ))}
            </OptionsContainer>
            {signUpOrigin === 'Other' && (
              <div>
                <p style={{ textAlign: 'left', width: '100%', marginBottom: 0 }}>
                  {activeFormSectionMapper[currentForm].label}
                </p>
                <Input
                  maxLength={50}
                  style={{ width: 250, fontSize: 16 }}
                  placeholder={
                    t(
                      'Tell us how you heard about Curipod',
                      'Tell us how you heard about Curipod',
                    ) ?? activeFormSectionMapper[currentForm].placeHolderText
                  }
                  onChange={e => setCustomSignUpOrigin(e.target.value)}
                  value={customSignUpOrigin}
                />
              </div>
            )}
            <Button
              disabled={isSubmitting}
              loading={isSubmitting}
              onClick={() => handleGoForward()}
            >
              <T keyName="Next" defaultValue="Next" />
            </Button>
          </FormContainer>
        );

      case 'studentJoinForm':
        return (
          <FormContainer>
            <h1 style={{ textAlign: 'center' }}>
              <T
                keyName="Jump into today's learning adventure with rocket-powered curiosity"
                defaultValue="Jump into today's learning adventure with rocket-powered curiosity"
              />
            </h1>
            <div style={{ display: 'flex', gap: 20 }}>
              <RocketContainer>
                <CuriRocketSvg />
                <CuriRocketBigFlameSvg />
              </RocketContainer>
              <a
                href={`https://${configureStore().config.game.url}`}
                style={{
                  fontWeight: 500,
                  fontSize: 18,
                  color: theme.colors.greyLight,
                  marginRight: 20,
                }}
              >
                <Button
                  styles={{
                    backgroundColor: theme.colors.blue,
                    borderColor: theme.colors.blue,
                    borderRadius: '20px',
                    height: '40px',
                    width: '150px',
                  }}
                  onClick={() => {
                    metrics.logEvent('RedirectStudentFromOnboarding.Curi.live.EnterPin');
                  }}
                >
                  <T keyName="Enter pin" defaultValue="Enter pin" />
                </Button>
              </a>
            </div>
          </FormContainer>
        );

      default:
        return null;
    }
  };

  const renderMultiSelectForm = (
    formType: 'subjects' | 'grades',
    values: any[] | undefined,
    setValues: (values: any[] | undefined) => void,
    customValue: string | undefined,
    setCustomValue: (value: string) => void,
    trackingEvent: string,
    isSubmitting: boolean,
  ) => {
    const shouldDescribeCustomOption = values?.includes('Other');

    const allOptionsSelected =
      values && values.length >= activeFormSectionMapper[formType]?.options.length - 1;

    const handleSelect = (option: string) => {
      if (isSubmitting) return;

      metrics.logEvent(trackingEvent, { selectedOption: option });

      if (values?.includes(option)) {
        setValues(
          values.filter(s => s !== option).length === 0
            ? undefined
            : values.filter(s => s !== option),
        );
        return;
      }
      if (values === undefined) {
        setValues([option]);
      } else {
        setValues([...values, option]);
      }
    };

    const handleSelectAll = () => {
      if (isSubmitting) return;

      const allOptionsExceptOther = activeFormSectionMapper[formType]?.options
        .map(o => o.value)
        .filter(o => o !== 'Other');
      setValues(allOptionsExceptOther);
      metrics.logEvent('Onboarding.SelectAll', {
        currentOnboardingPage: formType,
      });
    };

    const handleDeselectAll = () => {
      if (isSubmitting) return;

      setValues(undefined);
      metrics.logEvent('Onboarding.DeselectAll', {
        currentOnboardingPage: formType,
      });
    };

    return (
      <FormContainer>
        <h1>{activeFormSectionMapper[formType].title}</h1>
        <OptionsContainer>
          {activeFormSectionMapper[formType].options &&
            activeFormSectionMapper[formType].options.map((option, i) => (
              <StyledOption
                $isSelected={values?.includes(option.value) || false}
                key={i}
                onClick={() => handleSelect(option.value)}
              >
                {option.displayValue}
              </StyledOption>
            ))}
        </OptionsContainer>
        {shouldDescribeCustomOption && (
          <div>
            <p style={{ textAlign: 'left', width: '100%', marginBottom: 0 }}>
              {activeFormSectionMapper[formType].label}
            </p>
            <Input
              maxLength={50}
              style={{ width: 250, fontSize: 16 }}
              placeholder={
                activeFormSectionMapper[formType].placeHolderText ??
                t('Enter your grade(s)', 'Enter your grade(s)')
              }
              onChange={e => setCustomValue(e.target.value)}
              value={customValue}
            />
          </div>
        )}
        <Button
          disabled={isSubmitting}
          loading={isSubmitting}
          onClick={() => handleGoForward()}
        >
          <T keyName="Next" defaultValue="Next" />
        </Button>
        <Button
          type="ghost"
          disabled={isSubmitting}
          styles={{
            border: 'none',
            color: theme.colors.primary,
            background: theme.colors.white,
            padding: 0,
            boxShadow: 'none',
          }}
          onClick={allOptionsSelected ? handleDeselectAll : handleSelectAll}
        >
          {allOptionsSelected ? (
            <T keyName="Deselect All" defaultValue="Deselect All" />
          ) : (
            <T keyName="Select All" defaultValue="Select All" />
          )}
        </Button>
      </FormContainer>
    );
  };

  return (
    <Modal
      open={open}
      onCancel={isSubmitting ? undefined : onClose}
      footer={null}
      width={windowWidth < 600 ? '95%' : 900}
      closable={false}
      maskClosable={false}
      keyboard={false}
      className="custom-positioned-modal"
      style={
        windowWidth < 600
          ? {
              top: '20px',
              bottom: '20px',
              height: 'calc(100vh - 40px)',
              maxHeight: 'none',
            }
          : windowHeight < 600
          ? { top: '50%', transform: 'translateY(-50%)' }
          : { top: 'calc(50vh - 300px)' }
      }
    >
      <ModalContent>
        <InfoBar
          handleGoBack={handleGoBack}
          showProgress={role !== 'Student'}
          canGoBack={currentForm !== 'personalInfo' && !isSubmitting}
          canSkipCurrentForm={canSkipCurrentForm && !isSubmitting}
          handleGoForward={handleGoForward}
          formNumber={formNumber}
          isSubmitting={isSubmitting}
        />
        {renderCurrentForm()}
      </ModalContent>
    </Modal>
  );
};

const ModalContent = styled.div`
  padding: 20px;

  @media (max-width: 768px) {
    height: 100%;
    overflow-y: visible;
    padding: 10px;
  }
`;

const FormContainer = styled.div`
  width: 100%;
  display: flex;
  gap: 30px;
  flex-direction: column;
  align-items: center;
  justify-content: flex-start;
  overflow: visible;
`;

const PersonalInfoContainer = styled.div`
  display: flex;
  gap: 20px;
  flex-direction: column;
  align-items: center;
`;

const OptionsContainer = styled.div`
  width: 100%;
  display: flex;
  flex-wrap: wrap;
  gap: 10px 15px;
  align-items: center;
  justify-content: center;
`;

const StyledOption = styled.div<{ $isSelected: boolean }>`
  padding: 10px 20px;
  border-radius: 30px;
  background-color: ${theme.colors.white};
  color: ${theme.colors.black};
  font-weight: 600;
  font-size: 16px;
  text-align: center;
  border: 1px solid ${theme.colors.black};
  cursor: pointer;
  transition: 0.2s ease-in-out;

  &:hover {
    background-color: ${theme.colors.grey};
    color: ${({ $isSelected }) =>
      $isSelected ? theme.colors.green : theme.colors.black};
    border: 1px solid ${theme.colors.white};
  }

  ${({ $isSelected }) =>
    $isSelected &&
    css`
      color: ${theme.colors.green};
      border: 1px solid ${theme.colors.green};
    `}
`;

const RocketContainer = styled.div`
  position: relative;
  width: 0%;
  height: 40px;
  width: 0px;
  svg {
    height: 40px;
    width: 40px;
    position: absolute;
    right: 0px;
    top: 0px;
    transform: rotate(0deg);
    transform-origin: center;
  }

  svg:last-child {
    animation: flames 0.2s infinite linear;
  }

  @keyframes flames {
    0% {
      opacity: 0;
    }
    50% {
      opacity: 1;
    }
    100% {
      opacity: 0;
    }
  }
`;

const InfoBar = ({
  handleGoBack,
  canGoBack,
  canSkipCurrentForm,
  handleGoForward,
  showProgress,
  formNumber,
  isSubmitting,
}: {
  handleGoBack: () => void;
  canGoBack: boolean;
  canSkipCurrentForm: boolean;
  handleGoForward: (isSkip?: boolean) => void;
  showProgress: boolean;
  formNumber: number;
  isSubmitting?: boolean;
}) => {
  const { t } = useTranslate();
  const isLargerThan500 = useBreakPoint(500);

  return (
    <InfoBarContainer>
      {canGoBack && (
        <Button
          styles={{
            border: 'none',
            position: 'absolute',
            left: 10,
            top: 10,
            background: 'none',
            boxShadow: 'none',
          }}
          icon={<ChevronLeft color={theme.colors.black} />}
          onClick={() => {
            handleGoBack();
          }}
          type="ghost"
          disabled={isSubmitting}
        >
          {isLargerThan500 ? t('Go back') : ' '}
        </Button>
      )}
      <ProgressBarContainer $showProgress={showProgress}>
        <h2 style={{ gridColumn: '1 / -1', justifySelf: 'center', marginBottom: 0 }}>
          {isSubmitting ? (
            <T keyName="Saving your profile..." defaultValue="Saving your profile..." />
          ) : (
            <T keyName="Complete Your Profile" defaultValue="Complete Your Profile" />
          )}
        </h2>
        {showProgress && (
          <>
            <ProgressBar $isComplete={formNumber >= 0} />
            <ProgressBar $isComplete={formNumber >= 1} />
            <ProgressBar $isComplete={formNumber >= 2} />
            <ProgressBar $isComplete={formNumber >= 3} />
            <ProgressBar $isComplete={formNumber >= 4} />
            <ProgressBar $isComplete={formNumber >= 5} />
          </>
        )}
      </ProgressBarContainer>
      {canSkipCurrentForm && (
        <Button
          styles={{
            border: 'none',
            position: 'absolute',
            right: 10,
            top: 10,
            flexDirection: 'row-reverse',
            background: 'none',
            boxShadow: 'none',
          }}
          icon={<ChevronRight color={theme.colors.black} />}
          onClick={() => handleGoForward(true)}
          type="ghost"
          disabled={isSubmitting}
        >
          {isLargerThan500 ? t('Skip') : ' '}
        </Button>
      )}
    </InfoBarContainer>
  );
};

const InfoBarContainer = styled.div`
  width: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
  margin-bottom: 10px;
`;

const ProgressBarContainer = styled.div<{ $showProgress: boolean }>`
  width: 320px;
  display: grid;
  grid-template-rows: ${({ $showProgress }) => ($showProgress ? '3fr 2fr' : '1fr')};
  grid-template-columns: repeat(7, 1fr);
  grid-gap: 5px;
  margin: ${({ $showProgress }) => ($showProgress ? '0px' : '20px 0px')};
  @media (max-width: 500px) {
    width: 240px;
  }
`;

const ProgressBar = styled.div<{ $isComplete: boolean }>`
  width: 100%;
  height: 5px;
  border-radius: 5px;
  background: ${theme.colors.orange} !important;
  opacity: ${({ $isComplete }) => ($isComplete ? 1 : 0.3)};
`;

export default OnboardingModal;
